import React, {useContext} from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Button from "react-bootstrap/Button";
import ProgressBar from "react-bootstrap/ProgressBar";
import Col from 'react-bootstrap/Col';
import Alert from 'react-bootstrap/Alert';
import {withRouter} from "react-router-dom";
import {RouteComponentProps} from "react-router";

import {WizardStepProps} from "../../wizard/Wizard";
import FatButton from "../../buttons/fat-button/FatButton";
import SchemaFileUpload from "./fileupload/SchemaFileUpload";
import {IdValidationState, UploadSchemaContext} from "./context/UploadSchemaContext";
import {UnsavedChangesConfirmation} from "../../../common/NavigationConfirmation";
import {UploadSchemaWizardLeavingConfirmationMessage} from "./UploadSchemaWizard";
import SchemaMetadataWidget from "../../widgets/schema-metadata/SchemaMetadataWidget";
import SchemaInformationWidget from "../../widgets/schema-information/SchemaInformationWidget";
import SomlService from "../../../services/soml-service";
import {FORBIDDEN, MISSING_SCHEMA} from "../../../services/errorcodes";
import Loader from "../../loader/Loader";

/**
 * Step implementation dealing with uploading of SOML schema yaml file.
 */
class SelectSchemaStep extends React.Component<RouteComponentProps & WizardStepProps> {

  static contextType = UploadSchemaContext;

  constructor(props: any) {
    super(props);
    this.afterUpload = this.afterUpload.bind(this);
  }

  onProceed = () => {
    if (this.context.file && !this.context.uploading) {
      this.props.next(true);
    }
  };

  onReject = () => {
    this.props.history.goBack();
  };

  private isMissingSchemaError(error: any) {
    return error.code && error.code === MISSING_SCHEMA;
  }

  private isForbidden(error: any) {
    return error.code && error.code === FORBIDDEN;
  }

  private afterUpload() {
    if (!this.context.schemaYaml) {
      this.context.getSchema(this.context.file.path).then(() => {
        if (this.context.schemaId) {
          const rawId = this.context.uploadedSchema && this.context.uploadedSchema.rawId;
          if (rawId && !rawId.startsWith('/soml')) {
            this.context.setIdValidationState(IdValidationState.WRONG_ID);
          } else {
            // We have an id, so try to get a schema.
            SomlService.getSchema(this.context.schemaId).then((schema) => {
              // Schema with that id already exists, so we have id duplication and the existing
              // schema will be updated.
              if (schema) {
                this.context.setIdValidationState(IdValidationState.DUPLICATED_ID);
              }
            }).catch((error) => {
              // No schema was found with that id, so the schema will be created as a new one.
              if (this.isMissingSchemaError(error)) {
                this.context.setIdValidationState(IdValidationState.VALID_ID);
              }
              if (this.isForbidden(error)) {
                this.context.setIdValidationState(IdValidationState.CANT_VALIDATE);
                this.context.showError(error);
              }
            });
          }
        } else {
          this.context.setIdValidationState(IdValidationState.MISSING_ID);
        }
      });
    }
  }

  render() {
    const {uploading, progress, file, error, idValidationState, isRequestRunning} = this.context;
    const idValidated = idValidationState !== IdValidationState.NONE;
    const proceedDisabled = !file || !idValidated || error;
    const loader = isRequestRunning ? <Loader/> : false;
    return (
      <>
      <Container className="SelectSchemaStep no-padding" fluid>
        <Row className="pb-3" noGutters>
          <Col>
            <div className="StepActions">
              <Button className="reject prev-btn"
                      disabled={false}
                      onClick={this.onReject}
                      title="Reject">
                Reject
              </Button>
              <Button className="proceed next-btn"
                      disabled={proceedDisabled}
                      onClick={this.onProceed}
                      title="Proceed">
                Proceed
              </Button>
              {loader}
            </div>
          </Col>
        </Row>
        <Row xl={3} lg={3} md={2} sm={1} xs={1}>
          <Col>
            <SchemaFileUpload afterUpload={this.afterUpload}/>
          </Col>
          <Col>
            <FatButton className="upload-url-btn" icon="link"
                       title="Get Schema from URL"
                       descr="Resource should contain a valid yaml content"/>
          </Col>
          <Col>
            <FatButton className="import-snippet-btn" icon="pencil-alt"
                       title="Import Schema snippet"
                       descr="Type or paste yaml schema"/>
          </Col>
        </Row>
        <Row>
          <Col className="pt-3">{
            uploading ?
              <ProgressBar variant="success" min={0} max={100} now={progress}
                           label={Math.round(progress) + '%'}/>
              : ''
          }
          </Col>
        </Row>
        <SchemaIdValidationInfo schemaId={this.context.schemaId}
                                schema={this.context.schema}
                                idValidationState={this.context.idValidationState}/>
        <SchemaWidgets/>
      </Container>
      <UnsavedChangesConfirmation when={this.context.file}
                                  message={UploadSchemaWizardLeavingConfirmationMessage}/>
      </>
    );
  }
}

function SchemaIdValidationInfo(props: { schemaId: string, schema: string, idValidationState: IdValidationState }) {
  let message;
  if (props.idValidationState === IdValidationState.MISSING_ID || props.idValidationState === IdValidationState.WRONG_ID) {
    message = 'Schema with generated ID will be created.';
  } else if (props.idValidationState === IdValidationState.DUPLICATED_ID) {
    message = `Schema with "${props.schemaId}" ID exists on the server. If you proceed the schema will be updated.`;
  } else if (props.idValidationState === IdValidationState.VALID_ID) {
    message = `Schema with "${props.schemaId}" ID will be created.`;
  }
  if (props.schema && message) {
    return (
      <Row>
        <Col>
          <Alert variant="info" className="SchemaIdValidationInfo text-break">{message}</Alert>
        </Col>
      </Row>
    );
  }
  return null;
}

function SchemaWidgets() {
  const context = useContext(UploadSchemaContext);
  const {loading, schemaYaml} = context;
  // The yaml schema should be already present in the context as it's loaded after the upload.
  if (!schemaYaml) {
    return null;
  }
  return <Row className="py-3" xl={2} lg={2} md={2} sm={1} xs={1}>
    <Col>
      <SchemaMetadataWidget schema={schemaYaml} loading={loading}/>
    </Col>
    <Col>
      <SchemaInformationWidget schema={schemaYaml} loading={loading}/>
    </Col>
  </Row>
}

export default withRouter(SelectSchemaStep);
